import {
  restartTizenScreen,
  updateTizenFirmware,
  updateTizenSchedule,
  captureTizenScreenshot,
  changeTizenTimezone,
} from '@/utils/tizen';
import {
  restartWebOSDevice,
  updateWebOSSchedule,
  captureWebOsScreenshot,
  changeWebOSTimezone,
  changeWebOSNtpServer,
} from '@/utils/webOS';
import { logger } from '@/utils/screenEventsLogger';
import { uploadScreenshot } from '@/api/screenEvent';
import { uploadOfflineScreenshot } from '@/api/offlineEvents';
import { isLoggedIn } from '@/services/auth';
import { UNIQUE_SCREEN_IDENTIFIER } from '@/constants';
import { reloadPage } from '@/helpers/utils';
import { ON_OFF_SCHEDULES } from '@/constants';

function handleCacheCallback(message) {
  if (message === 'success') {
    logger.addLog('Cache cleared successfully on Android device');
    reloadPage();
  } else {
    logger.addLog('ERROR: Cache clearing failed - ' + message);
  }
}

export async function handleScreenshotCaptured(base64Screenshot) {
  if (!base64Screenshot) {
    logger.addLog('No Screenshot captured');
    return;
  }
  logger.addLog('Screenshot captured, uploading to backend');

  try {
    if (isLoggedIn()) {
      await uploadScreenshot(base64Screenshot);
      logger.addLog('Screenshot uploaded successfully');
    } else {
      let screenIdentifier = localStorage.getItem(UNIQUE_SCREEN_IDENTIFIER);
      if (!screenIdentifier) {
        logger.addLog('ERROR: No screen identifier found');
        return;
      }
      await uploadOfflineScreenshot(base64Screenshot, screenIdentifier);
      logger.addLog('Screenshot uploaded successfully');
    }
  } catch (error) {
    logger.addLog('ERROR: Failed to upload screenshot - ' + (error.message || 'Unknown error'));
  }
}

export function handleScreenshotError(errorMessage) {
  logger.addLog('ERROR: Screenshot capture failed - ' + errorMessage);
}

export function handleRestartDevice() {
  if (window.isAndroid && window.android && typeof window.android.restartDevice === 'function') {
    window.android.restartDevice();
  } else if (window.isTizen) {
    restartTizenScreen();
  } else if (window.isWebOS) {
    restartWebOSDevice();
  } else {
    logger.addLog(
      'ERROR: restartDevice function not available or not on Android or Tizen platform',
    );
  }
}

export function handleClearCache() {
  if (window.isAndroid && window.android && typeof window.android.clearCache === 'function') {
    window.onCacheCleared = handleCacheCallback;
    window.onCacheClearError = handleCacheCallback;
    logger.addLog('Triggering Android cache clear');
    window.android.clearCache();
  } else {
    logger.addLog('ERROR: clearCache function not available or not on Android platform');
  }
}

export function handleTakeScreenshot() {
  logger.addLog('Screenshot capture requested');
  try {
    if (window.isAndroid) {
      window.onScreenshotCaptured = handleScreenshotCaptured;
      window.onScreenshotError = handleScreenshotError;
      if (!window.android || typeof window.android.takeScreenshot !== 'function') {
        logger.addLog('ERROR: Screenshot capture not available');
        return;
      }
      window.android.takeScreenshot();
    } else if (window.isTizen) {
      captureTizenScreenshot(handleScreenshotCaptured);
    } else if (window.isWebOS) {
      captureWebOsScreenshot(handleScreenshotCaptured, handleScreenshotError);
    }
  } catch (error) {
    logger.addLog('ERROR: Screenshot capture failed - ' + error.message);
  }
}

export function handleUpdateFirmware(firmwareDetails) {
  logger.addLog('Firmware update requested');
  try {
    if (window.isTizen) {
      updateTizenFirmware(firmwareDetails);
    } else {
      logger.addLog('ERROR: updateFirmware function not available or not on Tizen platform');
    }
  } catch (error) {
    logger.addLog('ERROR: Firmware update failed - ' + error.message);
  }
}

export async function handleUpdateSchedule(scheduleDetails) {
  logger.addLog('Update schedule requested');
  try {
    if (window.isTizen) {
      updateTizenSchedule(scheduleDetails);
    } else if (window.isWebOS) {
      localStorage.setItem(ON_OFF_SCHEDULES, JSON.stringify(scheduleDetails));
      await updateWebOSSchedule(scheduleDetails);
    } else {
      logger.addLog('ERROR: updateSchedule function not available or not on Tizen platform');
    }
  } catch (error) {
    logger.addLog('ERROR: Update schedule failed - ' + error.message);
  }
}

export async function handleChangeTimezone(timezoneDetails) {
  logger.addLog('Change timezone requested');
  try {
    if (window.isTizen) {
      changeTizenTimezone(timezoneDetails);
    } else if (window.isWebOS) {
      await changeWebOSTimezone(timezoneDetails);
      await changeWebOSNtpServer(timezoneDetails);
    } else {
      logger.addLog('ERROR: changeTimezone function not available or not on Tizen platform');
    }
  } catch (error) {
    logger.addLog('ERROR: Change timezone failed - ' + error.message);
  }
}
